/**
 * AI edits store (R1 W2).
 *
 * Holds two things the agent store's events feed but that live outside it, to
 * keep that store from growing:
 *  - applied AI edits (`draft_edit_applied`) and the state of their Undo, which
 *    drive the one-line "Updated Methodology · Undo" notice in the panel and
 *    the editor;
 *  - the staged suggestions still waiting on the open document, which drive
 *    the per-document suggestion list.
 *
 * Nothing here is persisted: notices last for the page's lifetime, and the
 * suggestion list is rebuilt from every session load.
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { PendingSuggestion } from '@/lib/staged-suggestion'

// ===================
// Types
// ===================

/**
 * applied → undoing → undone | conflict | gone | failed. `failed` can be
 * retried; `conflict` (the section changed after the edit) and `gone` cannot.
 */
export type AppliedEditStatus = 'applied' | 'undoing' | 'undone' | 'conflict' | 'gone' | 'failed'

export interface AppliedAiEdit {
  /** The `direct` proposal row; Undo addresses it. */
  proposalId: string
  /** Session the edit was made in; Undo's PATCH goes through it. */
  sessionId: string
  segmentId: string | null
  /** The section key the write addressed; Undo's PATCH path uses it. */
  sectionId: string
  heading: string | null
  /** Segment version the edit produced. */
  version: number | null
  /** Turn that made the edit (a later turn's apply with this id = undone from chat). */
  turnId: string
  /** Panel reply the notice hangs under; null when no reply existed yet. */
  messageId: string | null
  status: AppliedEditStatus
  /** Hidden from the editor notice (the panel line stays as the record). */
  dismissed: boolean
}

export type NewAppliedAiEdit = Omit<AppliedAiEdit, 'status' | 'dismissed'>

interface AiEditsState {
  edits: AppliedAiEdit[]
  suggestions: PendingSuggestion[]

  /** Record an applied edit. A replayed event (same proposalId) is ignored. */
  recordEdit: (edit: NewAppliedAiEdit) => void
  setEditStatus: (proposalId: string, status: AppliedEditStatus) => void
  dismissEdit: (proposalId: string) => void
  /**
   * A `draft_patch_applied` naming an edit's proposal from a LATER turn is the
   * chat `undo_ai_edit` tool undoing it. The edit's own apply event (same turn)
   * is not.
   */
  noteDraftApplied: (proposalId: string, turnId: string) => void

  /** Replace the list (every session load). */
  setSuggestions: (suggestions: PendingSuggestion[]) => void
  /** Add or replace a suggestion. One pending suggestion per section stays the rule. */
  upsertSuggestion: (suggestion: PendingSuggestion) => void
  removeSuggestion: (proposalId: string) => void

  reset: () => void
}

const initialState = {
  edits: [] as AppliedAiEdit[],
  suggestions: [] as PendingSuggestion[],
}

// ===================
// Store
// ===================

export const useAiEditsStore = create<AiEditsState>()(
  devtools(
    (set) => ({
      ...initialState,

      recordEdit: (edit) => {
        set((state) => (
          state.edits.some((existing) => existing.proposalId === edit.proposalId)
            ? state
            : { edits: [...state.edits, { ...edit, status: 'applied', dismissed: false }] }
        ))
      },

      setEditStatus: (proposalId, status) => {
        set((state) => ({
          edits: state.edits.map((edit) => (edit.proposalId === proposalId ? { ...edit, status } : edit)),
        }))
      },

      dismissEdit: (proposalId) => {
        set((state) => ({
          edits: state.edits.map((edit) => (edit.proposalId === proposalId ? { ...edit, dismissed: true } : edit)),
        }))
      },

      noteDraftApplied: (proposalId, turnId) => {
        set((state) => {
          const target = state.edits.find((edit) => edit.proposalId === proposalId)
          if (!target || target.turnId === turnId || target.status === 'undone') return state
          return {
            edits: state.edits.map((edit) => (
              edit.proposalId === proposalId ? { ...edit, status: 'undone' as const } : edit
            )),
          }
        })
      },

      setSuggestions: (suggestions) => {
        // Defensive: keep the newest entry per section if the source repeats one.
        const bySection = new Map<string, PendingSuggestion>()
        for (const suggestion of suggestions) {
          if (!bySection.has(suggestion.sectionId)) bySection.set(suggestion.sectionId, suggestion)
        }
        set({ suggestions: [...bySection.values()] })
      },

      upsertSuggestion: (suggestion) => {
        set((state) => ({
          suggestions: [
            ...state.suggestions.filter((existing) => (
              existing.sectionId !== suggestion.sectionId && existing.proposalId !== suggestion.proposalId
            )),
            suggestion,
          ],
        }))
      },

      removeSuggestion: (proposalId) => {
        set((state) => {
          if (!state.suggestions.some((suggestion) => suggestion.proposalId === proposalId)) return state
          return { suggestions: state.suggestions.filter((suggestion) => suggestion.proposalId !== proposalId) }
        })
      },

      reset: () => set(initialState),
    }),
    { name: 'ai-edits-store' }
  )
)

// ===================
// Event parsing
// ===================

/** Parse a `draft_edit_applied` payload; null when it is malformed. */
export function parseDraftEditApplied(
  data: Record<string, unknown>,
  context: { sessionId: string; turnId: string; messageId: string | null },
): NewAppliedAiEdit | null {
  const proposalId = data['proposalId']
  const sectionId = data['sectionId']
  if (typeof proposalId !== 'string' || proposalId.length === 0) return null
  if (typeof sectionId !== 'string' || sectionId.length === 0) return null
  const heading = data['heading']
  const segmentId = data['segmentId']
  const version = data['version']
  return {
    proposalId,
    sessionId: context.sessionId,
    segmentId: typeof segmentId === 'string' ? segmentId : null,
    sectionId,
    heading: typeof heading === 'string' && heading.trim() ? heading : null,
    version: typeof version === 'number' ? version : null,
    turnId: context.turnId,
    messageId: context.messageId,
  }
}
